import { Box, Flex, Icon, Text, useBreakpointValue, useDisclosure } from '@chakra-ui/react';
import { FaPaperclip } from '@react-icons/all-files/fa/FaPaperclip';
import { useRef, useState } from 'react';

import CollapsibleCard from '../../../../../../components/CollapsibleCard';
import Button from '../../../../../../components/ui/buttons/Button';
import { useFilePicker } from '../../../../../../hooks';
import { useMarketplaceExternalId } from '../../../../../../services/marketplace/externalId';
import { useGetLeadByExternalIdPhotos } from '../../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalIdPhotos';
import { pluralize } from '../../../../../../utils/helpers';
import MarketplaceCard from '../../MarketplaceCard';
import OdometerMileageModal from './OdometerMileageModal';
import OtherDocumentRow from './documents/OtherDocumentRow';
import PhotoRow from './documents/PhotoRow';
import RequiredDocumentRow from './documents/RequiredDocumentRow';
import RequiredPhotoRow from './documents/RequiredPhotoRow';
import UploadFileModal from './documents/UploadFileModal';
import { useDocumentUpload } from './useDocumentUpload';
import { ACCEPTED_FILE_TYPES, UploadKind } from './utils';

type PostAcceptanceDocumentsPanelProps = {
  dealId: string;
  defaultCollapsed: boolean;
};

const PostAcceptanceDocumentsPanel = ({
  dealId,
  defaultCollapsed,
}: PostAcceptanceDocumentsPanelProps) => {
  const externalId = useMarketplaceExternalId(dealId);
  const {
    documents,
    documentsReady,
    checklist,
    isProcessing,
    upload,
    refetch,
    isOdometerPromptOpen,
    confirmOdometerUpload,
    cancelOdometerUpload,
  } = useDocumentUpload(dealId);
  const photosQuery = useGetLeadByExternalIdPhotos({ externalId });
  const photos = photosQuery.data ?? [];
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const [openOverride, setOpenOverride] = useState<boolean | null>(null);
  const isOpen = openOverride ?? (!isMobile && !defaultCollapsed);
  const uploadModal = useDisclosure();

  const pendingUploadRef = useRef<{ kind: UploadKind; key: string }>({
    kind: 'document',
    key: 'other',
  });
  const { fileInputRef, openPicker, handleFileSelected } = useFilePicker((file) =>
    upload(pendingUploadRef.current.kind, pendingUploadRef.current.key, file),
  );

  const pickUpload = (kind: UploadKind, key: string) => {
    pendingUploadRef.current = { kind, key };
    openPicker();
  };

  const handleToggle = (next: boolean) => {
    setOpenOverride(next);
    // Presigned document URLs are short-lived; freshen them on expand.
    if (next) refetch();
  };

  const requiredDocuments = documents.filter((document) => document.documentType !== 'other');
  const otherDocuments = documents.filter((document) => document.documentType === 'other');
  const requiredPhotos = photos.filter((photo) => photo.photoType !== 'standard');
  const otherPhotos = photos.filter((photo) => photo.photoType === 'standard');
  const fileCount = documents.length + photos.length;
  const isHydrated = documentsReady && !!photosQuery.data;

  return (
    <MarketplaceCard bg="grayLightest" p={0} overflow="hidden">
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES}
        hidden
        onChange={handleFileSelected}
      />
      <CollapsibleCard
        isOpen={isOpen}
        onToggle={handleToggle}
        header={
          <>
            <Text fontFamily="heading" fontSize="14px" fontWeight="700" color="leaseEndBlue">
              Your files
            </Text>
            {isHydrated && (
              <Text fontSize="12px" fontWeight="600" color="grayMedium">
                {fileCount} {pluralize('file', fileCount)}
              </Text>
            )}
          </>
        }
      >
        <Box px="18px" pb="14px">
          {isHydrated && fileCount === 0 && (
            <Text fontSize="13px" lineHeight="1.5" color="grayMedium">
              Nothing here yet. Every file on your sale, and anything you upload, is kept here.
            </Text>
          )}
          {fileCount > 0 && (
            <Flex direction="column" gap="6px">
              {requiredDocuments.map((document) => (
                <RequiredDocumentRow
                  key={document.id}
                  document={document}
                  isReplacing={isProcessing(document.documentType)}
                  onReplace={() => pickUpload('document', document.documentType)}
                />
              ))}
              {requiredPhotos.map((photo) => (
                <RequiredPhotoRow
                  key={photo.id}
                  photo={photo}
                  isReplacing={isProcessing(photo.photoType)}
                  onReplace={() => pickUpload('photo', photo.photoType)}
                />
              ))}
              {otherDocuments.map((document) => (
                <OtherDocumentRow key={document.id} document={document} />
              ))}
              {otherPhotos.map((photo) => (
                <PhotoRow key={photo.id} photo={photo} />
              ))}
            </Flex>
          )}

          <Button
            variant="dashed"
            w="100%"
            mt="12px"
            leftIcon={<Icon as={FaPaperclip} boxSize="14px" />}
            isDisabled={!externalId}
            onClick={uploadModal.onOpen}
          >
            Upload a file
          </Button>
        </Box>
      </CollapsibleCard>
      <UploadFileModal
        isOpen={uploadModal.isOpen}
        onClose={uploadModal.onClose}
        checklist={checklist}
        upload={upload}
        isProcessing={isProcessing}
      />
      {externalId && (
        <OdometerMileageModal
          isOpen={isOdometerPromptOpen}
          externalId={externalId}
          onConfirm={confirmOdometerUpload}
          onCancel={cancelOdometerUpload}
        />
      )}
    </MarketplaceCard>
  );
};

export default PostAcceptanceDocumentsPanel;
